// Sistema ibrido: Gemini AI (primario) + LastFM (fallback)

import { getSimilarArtistsFromGemini, isGeminiEnabled } from './gemini';

// Last.fm: la API key è pensata per uso client (rate limit per account). Preferisci env; fallback per non rompere random in locale.
const LASTFM_API_KEY =
  process.env.REACT_APP_LASTFM_API_KEY ||
  process.env.LASTFM_API_KEY ||
  'ccaf6ed7bb0746aa31c644eede7eb183';
const LASTFM_BASE_URL = 'https://ws.audioscrobbler.com/2.0/';


// Cache per ricordare gli artisti già mostrati per ogni artista
const artistCache = new Map();

// Esponi cache globalmente per debugging
if (typeof window !== 'undefined') {
  window.lastfmArtistCache = artistCache;
}

// Funzione per resettare la cache di un artista (per ripartire da zero nelle ricerche)
export const resetArtistCache = (artistName) => {
  const cacheKey = artistName.toLowerCase();
  if (artistCache.has(cacheKey)) {
    const cached = artistCache.get(cacheKey);
    cached.currentIndex = 0; // Reset dell'indice di paginazione
    console.log(`🔄 [LastFM] Cache resettata per "${artistName}" - riparte da zero`);
  }
};



// Funzione PRIVATA per ottenere artisti simili solo da LastFM
export const getSimilarArtistsFromLastFM = async (artistName) => {
  console.log(`🔄 [LastFM] Ricerca artisti simili per "${artistName}"...`);
  
  const url = `${LASTFM_BASE_URL}?method=artist.getSimilar&artist=${encodeURIComponent(artistName)}&api_key=${LASTFM_API_KEY}&format=json&limit=50`;
  
  const response = await fetch(url);
  const data = await response.json();
  
  if (data.error) {
    throw new Error(data.message || 'Artista non trovato');
  }

  const artists = data.similarartists?.artist || [];
  
  if (artists.length === 0) {
    throw new Error('Nessun artista simile trovato');
  }
  
  // Filtra solo artisti con match > 0.05 (5%) e ordina per percentuale (più simile al meno simile)
  const validArtists = artists
    .filter(artist => {
      const match = parseFloat(artist.match || 0);
      return match > 0.05;
    })
    .sort((a, b) => parseFloat(b.match || 0) - parseFloat(a.match || 0))
    .slice(0, 50); // Prendi esattamente 50 artisti
  
  console.log(`💾 [LastFM] Restituisco ${validArtists.length} artisti simili per "${artistName}"`);
  
  return validArtists;
};

// Funzione PUBBLICA per ottenere artisti simili (sistema ibrido: Gemini + LastFM fallback)
export const getSimilarArtists = async (artistName) => {
  try {
    // 1. Prova prima con Gemini AI (se abilitato)
    if (isGeminiEnabled()) {
      console.log(`%c🤖 GEMINI AI - Tentativo per "${artistName}"`, 'background: #4285f4; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;');
      
      const geminiResults = await getSimilarArtistsFromGemini(artistName);
      
      if (geminiResults && geminiResults.length > 0) {
        console.log(`%c✅ RISPOSTA DA: GEMINI AI - ${geminiResults.length} artisti per "${artistName}"`, 'background: #0f9d58; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold; font-size: 14px;');
        console.log(`%c📊 Fonte: Google Gemini AI (Multi-Model Cascade)`, 'color: #4285f4; font-weight: bold;');
        return geminiResults;
      }
      
      console.log(`%c⚠️ Gemini AI non disponibile - FALLBACK a LastFM`, 'background: #f4b400; color: black; padding: 4px 8px; border-radius: 4px; font-weight: bold;');
    } else {
      console.log(`%c🔒 GEMINI AI DISABILITATO - Uso LastFM`, 'background: #666; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;');
    }
    
    // 2. Fallback a LastFM (se Gemini è disabilitato o ha fallito)
    console.log(`%c🎵 LASTFM - Caricamento per "${artistName}"`, 'background: #d51007; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;');
    
    // Delay di 3 secondi per simulare ricerca (anche per LastFM)
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    const lastfmResults = await getSimilarArtistsFromLastFM(artistName);
    console.log(`%c✅ RISPOSTA DA: LASTFM - ${lastfmResults.length} artisti per "${artistName}"`, 'background: #d51007; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold; font-size: 14px;');
    console.log(`%c📊 Fonte: Last.fm API`, 'color: #d51007; font-weight: bold;');
    return lastfmResults;
    
  } catch (error) {
    console.error(`❌ [Hybrid] Errore getSimilarArtists per "${artistName}":`, error);
    throw error;
  }
};

// Funzione per cercare artisti con filtri
export const searchArtists = async (query, genre = '', year = '', limit = 10) => {
  try {
    let url = `${LASTFM_BASE_URL}?method=artist.search&artist=${encodeURIComponent(query)}&api_key=${LASTFM_API_KEY}&format=json&limit=${limit}`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.message || 'Errore nella ricerca');
    }
    
    return data.results?.artistmatches?.artist || [];
  } catch (error) {
    throw error;
  }
};

// Funzione per autocompletamento artisti (ricerca veloce)
export const searchArtistSuggestions = async (query, limit = 5) => {
  try {
    if (!query || query.trim().length < 2) {
      return [];
    }
    
    // Prende più risultati per poter ordinare meglio
    const searchLimit = Math.max(limit * 4, 20);
    
    // Per query corte come "beatl", prova anche varianti comuni
    let searchQueries = [query.trim()];
    const queryLower = query.toLowerCase().trim();
    
    // Aggiungi varianti comuni per artisti famosi
    if (queryLower === 'beatl' || queryLower === 'beatle') {
      searchQueries.push('Beatles', 'The Beatles');
    } else if (queryLower === 'queen' || queryLower === 'quee') {
      searchQueries.push('Queen');
    } else if (queryLower === 'eminem' || queryLower === 'emin') {
      searchQueries.push('Eminem');
    }
    
    // Fai ricerche multiple e combina i risultati
    let allArtists = [];
    
    for (const searchQuery of searchQueries) {
      const url = `${LASTFM_BASE_URL}?method=artist.search&artist=${encodeURIComponent(searchQuery)}&api_key=${LASTFM_API_KEY}&format=json&limit=${searchLimit}`;
      
      try {
        const response = await fetch(url);
        const data = await response.json();
        
        if (!data.error && data.results?.artistmatches?.artist) {
          const artists = Array.isArray(data.results.artistmatches.artist) 
            ? data.results.artistmatches.artist 
            : [data.results.artistmatches.artist];
          
          allArtists.push(...artists);
        }
      } catch (err) {

      }
    }
    
    if (allArtists.length === 0) {
      return [];
    }
    
    // Rimuovi duplicati basati sul nome
    const uniqueArtists = [];
    const seenNames = new Set();
    
    for (const artist of allArtists) {
      const normalizedName = artist.name.toLowerCase().trim();
      if (!seenNames.has(normalizedName)) {
        seenNames.add(normalizedName);
        uniqueArtists.push(artist);
      }
    }
    
    // Converte e ordina per listeners (più ascoltati primi)
    const artistsWithListeners = uniqueArtists.map(artist => ({
      name: artist.name,
      listeners: artist.listeners || '0',
      listenersNum: parseInt(artist.listeners || '0')
    }));
    

    
    // Ordina per numero di listeners (decrescente) 
    const sortedArtists = artistsWithListeners.sort((a, b) => {
      const queryLower = query.toLowerCase();
      const aNameLower = a.name.toLowerCase();
      const bNameLower = b.name.toLowerCase();
      
      // Rimuovi articoli comuni per matching migliore
      const cleanQuery = queryLower.replace(/^(the |a |an )/i, '');
      const aNameClean = aNameLower.replace(/^(the |a |an )/i, '');
      const bNameClean = bNameLower.replace(/^(the |a |an )/i, '');
      
      // Prima priorità: match esatto (con e senza articoli)
      const aExactMatch = aNameLower === queryLower || aNameClean === cleanQuery;
      const bExactMatch = bNameLower === queryLower || bNameClean === cleanQuery;
      
      if (aExactMatch && !bExactMatch) return -1;
      if (!aExactMatch && bExactMatch) return 1;
      
      // Seconda priorità: nome pulito inizia con query pulita
      const aStartsWithClean = aNameClean.startsWith(cleanQuery);
      const bStartsWithClean = bNameClean.startsWith(cleanQuery);
      
      if (aStartsWithClean && !bStartsWithClean) return -1;
      if (!aStartsWithClean && bStartsWithClean) return 1;
      
      // Terza priorità: match normale che inizia con la query
      const aStartsWith = aNameLower.startsWith(queryLower);
      const bStartsWith = bNameLower.startsWith(queryLower);
      
      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;
      
      // Quarta priorità: contiene la query
      const aContains = aNameLower.includes(queryLower);
      const bContains = bNameLower.includes(queryLower);
      
      if (aContains && !bContains) return -1;
      if (!aContains && bContains) return 1;
      
      // Quinta priorità: numero di listeners
      return b.listenersNum - a.listenersNum;
    });
    
    // Prende solo i migliori risultati richiesti
    const finalResults = sortedArtists.slice(0, limit);
    return finalResults.map(artist => ({
      name: artist.name,
      listeners: artist.listeners
    }));
  } catch (error) {

    return [];
  }
};

// Cache per il numero totale di pagine (per evitare chiamate multiple)
let totalPagesCache = null;
let totalPagesCacheTime = 0;
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 ore

// Funzione per ottenere il numero totale di pagine disponibili
const getTotalPages = async () => {
  // Usa cache se valida
  if (totalPagesCache && Date.now() - totalPagesCacheTime < CACHE_DURATION) {
    return totalPagesCache;
  }

  try {
    // Chiama la prima pagina per ottenere i metadati sulla classifica globale
    // chart.getTopArtists = classifica globale degli artisti più popolari su Last.fm
    const url = `${LASTFM_BASE_URL}?method=chart.getTopArtists&api_key=${LASTFM_API_KEY}&format=json&limit=1&page=1`;
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.error) {
      console.warn('⚠️ [LastFM] Impossibile ottenere totale pagine, uso default 50');
      return 50; // Fallback
    }
    
    // Last.fm restituisce totalPages in artists.@attr.totalPages o artists.@attr.total
    // IMPORTANTE: Last.fm ha un limite massimo di 1,000,000 pagine, ma in realtà
    // le pagine disponibili con artisti reali sono molto meno (circa 100-200)
    const rawTotal = data.artists?.['@attr']?.totalPages || 
                     data.artists?.['@attr']?.total || 
                     50; // Fallback se non disponibile
    
    // Limita a 200 pagine per sicurezza (Last.fm ha molte pagine ma poche con artisti reali)
    const MAX_SAFE_PAGES = 200;
    const parsedTotal = parseInt(rawTotal, 10) || 50;
    // Usa il minimo tra il totale reale e il limite sicuro
    const totalPages = Math.min(parsedTotal, MAX_SAFE_PAGES);
    
    // Cache il risultato
    totalPagesCache = totalPages;
    totalPagesCacheTime = Date.now();
    
    console.log(`📊 [LastFM] Totale pagine disponibili: ${totalPagesCache} (limite sicuro: ${MAX_SAFE_PAGES}, raw: ${parsedTotal})`);
    return totalPagesCache;
  } catch (error) {
    console.warn('⚠️ [LastFM] Errore ottenendo totale pagine, uso default 50:', error);
    return 50; // Fallback
  }
};

// Funzione per ottenere artisti veramente random (restituisce 50 artisti)
export const getRandomArtists = async (limit = 30) => {
  if (!LASTFM_API_KEY) {
    throw new Error('Last.fm API key mancante (REACT_APP_LASTFM_API_KEY)');
  }

  const MAX_RETRIES = 5; // Massimo 5 tentativi con pagine diverse
  const safeLimit = Math.max(1, Math.min(limit, 50));
  
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      // Ottieni il numero totale di pagine disponibili
      const totalPages = await getTotalPages();
      
      // Genera una pagina casuale tra 1 e totalPages
      // Per i primi tentativi, preferisci pagine più basse (più probabili di avere artisti)
      let randomPage;
      if (attempt === 0) {
        // Primo tentativo: usa pagine basse (1-50) che hanno più artisti
        randomPage = Math.floor(Math.random() * Math.min(50, totalPages)) + 1;
      } else {
        // Retry: prova una pagina completamente casuale
        randomPage = Math.max(1, Math.min(Math.floor(Math.random() * totalPages) + 1, totalPages));
      }
      
      const url = `${LASTFM_BASE_URL}?method=chart.getTopArtists&api_key=${LASTFM_API_KEY}&format=json&limit=${safeLimit}&page=${randomPage}`;
      
      // SPIEGAZIONE: chart.getTopArtists è la classifica globale Last.fm
      // - Ogni pagina contiene fino a 50 artisti (noi chiediamo 30)
      // - Pagina 1 = artisti posizione 1-30 (i più popolari)
      // - Pagina 2 = artisti posizione 31-60
      // - Pagina 3 = artisti posizione 61-90
      // - ecc...
      // Scegliendo una pagina random, otteniamo artisti da una posizione casuale nella classifica
      console.log(`🎲 [LastFM] Tentativo ${attempt + 1}/${MAX_RETRIES}: caricamento ${limit} artisti dalla pagina ${randomPage} di ${totalPages} totali (artisti posizione ${(randomPage - 1) * safeLimit + 1}-${randomPage * safeLimit} nella classifica globale)...`);
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.error) {
        console.warn(`⚠️ [LastFM] Errore pagina ${randomPage}: ${data.message}`);
        // Se è un errore di pagina out of bounds, prova una pagina più bassa
        if (data.message?.includes('out of bounds')) {
          continue; // Prova con un'altra pagina
        }
        throw new Error(data.message || 'Errore nel caricamento artisti');
      }
      
      const artists = data.artists?.artist || [];
      
      // Se non ci sono artisti, prova con un'altra pagina
      if (artists.length === 0) {
        console.warn(`⚠️ [LastFM] Pagina ${randomPage} vuota, riprovo...`);
        continue;
      }
      
      // Randomizza l'ordine e prendi il numero richiesto
      const shuffled = artists.sort(() => Math.random() - 0.5);
      const final = shuffled.slice(0, safeLimit);
      
      console.log(`🎲 [LastFM] ✅ Restituisco ${final.length} artisti random (pagina ${randomPage}/${totalPages})`);
      
      return final;
    } catch (error) {
      // Se è l'ultimo tentativo, lancia l'errore
      if (attempt === MAX_RETRIES - 1) {
        console.error('❌ Errore getRandomArtists dopo tutti i tentativi:', error);
        throw error;
      }
      // Altrimenti continua con il prossimo tentativo
      console.warn(`⚠️ [LastFM] Tentativo ${attempt + 1} fallito, riprovo...`, error.message);
    }
  }
  
  // Se arriviamo qui, tutti i tentativi sono falliti
  throw new Error('Impossibile caricare artisti random dopo tutti i tentativi');
};

// Funzione per ottenere informazioni dettagliate di un artista
export const getArtistInfo = async (artistName) => {
  try {
    const url = `${LASTFM_BASE_URL}?method=artist.getInfo&artist=${encodeURIComponent(artistName)}&api_key=${LASTFM_API_KEY}&format=json`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.error) {

      throw new Error(data.message || 'Informazioni artista non disponibili');
    }
    
    return data.artist;
  } catch (error) {

    throw error;
  }
};

// Funzione per ottenere i top albums di un artista
export const getArtistTopAlbums = async (artistName, limit = 8) => {
  try {
    const response = await fetch(
      `${LASTFM_BASE_URL}?method=artist.getTopAlbums&artist=${encodeURIComponent(artistName)}&api_key=${LASTFM_API_KEY}&format=json&limit=${limit}`
    );
    
    const data = await response.json();
    
    if (data.error) {

      return [];
    }
    
    return data.topalbums?.album || [];
  } catch (error) {

    return [];
  }
};

// Funzione per ottenere una breve descrizione dell'artista
export const getArtistBio = async (artistName) => {
  try {
    const artistInfo = await getArtistInfo(artistName);
    const bio = artistInfo?.bio?.summary || artistInfo?.bio?.content || '';
    
    if (!bio) return '';
    
    // Pulisce il testo e lo accorcia
    let cleanBio = bio
      .replace(/<[^>]*>/g, '') // Rimuove tag HTML
      .replace(/\s+/g, ' ')     // Normalizza spazi
      .trim();
    
    // Tronca a circa 150 caratteri
    if (cleanBio.length > 150) {
      cleanBio = cleanBio.substring(0, 150);
      const lastSpace = cleanBio.lastIndexOf(' ');
      if (lastSpace > 100) {
        cleanBio = cleanBio.substring(0, lastSpace) + '...';
      } else {
        cleanBio = cleanBio + '...';
      }
    }
    
    return cleanBio;
  } catch (error) {

    return '';
  }
};

// Mappa dei codici lingua ai paesi per Last.fm
const countryMap = {
  'it': 'italy',
  'en': 'united states',
  'es': 'spain',
  'fr': 'france',
  'de': 'germany',
  'pt': 'portugal',
  'nl': 'netherlands',
  'pl': 'poland',
  'ru': 'russia',
  'ja': 'japan',
  'ko': 'south korea',
  'zh': 'china',
  'ar': 'saudi arabia',
  'tr': 'turkey',
  'sv': 'sweden',
  'no': 'norway',
  'da': 'denmark',
  'fi': 'finland',
  'is': 'iceland'
};

// Funzione per ottenere top artists per paese o globali (restituisce 20 artisti)
export const getTopArtists = async (limit = 20, country = null) => {
  try {
    let url;
    
    if (country && country !== 'global') {
      // Prova prima con geo.gettopartists per il paese specifico
      url = `${LASTFM_BASE_URL}?method=geo.gettopartists&country=${encodeURIComponent(country)}&api_key=${LASTFM_API_KEY}&format=json&limit=${limit}`;
    } else {
      // Usa chart.getTopArtists per classifica globale
      url = `${LASTFM_BASE_URL}?method=chart.getTopArtists&api_key=${LASTFM_API_KEY}&format=json&limit=${limit}`;
    }
    

    
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.error) {

      // Fallback alla classifica globale
      if (country && country !== 'global') {
        return getTopArtists(limit, 'global');
      }
      throw new Error(data.message || 'Errore nel caricamento top artists');
    }
    
    // Gestisce sia geo.gettopartists che chart.getTopArtists
    const artists = data.topartists?.artist || data.artists?.artist || [];
    

    
    if (artists.length === 0) {

      if (country && country !== 'global') {
        return getTopArtists(limit, 'global');
      }
      throw new Error('Nessun artista trovato');
    }
    
    // Prendi solo il numero richiesto
    const final = artists.slice(0, limit);
    

    return final;
  } catch (error) {

    throw error;
  }
};

// Funzione per rilevare il paese dalla lingua del browser
export const getCountryFromLanguage = () => {
  try {
    const language = navigator.language || navigator.languages?.[0] || 'en-US';
    const languageCode = language.split('-')[0].toLowerCase();
    
    const country = countryMap[languageCode];

    
    return country || null;
  } catch (error) {

    return null;
  }
};

// Funzione per rilevare il paese dall'IP (usando servizio esterno)
export const getCountryFromIP = async () => {
  try {
    console.log('🌍 Rilevamento paese da IP...');
    
    // Usa un servizio gratuito per rilevare il paese dall'IP
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    
    if (data.country_code) {
      const countryCode = data.country_code.toLowerCase();
      
      // Mappa i codici paese ISO ai nomi Last.fm
      const countryISOMap = {
        'it': 'italy',
        'us': 'united states',
        'es': 'spain', 
        'fr': 'france',
        'de': 'germany',
        'pt': 'portugal',
        'nl': 'netherlands',
        'pl': 'poland',
        'ru': 'russia',
        'jp': 'japan',
        'kr': 'south korea',
        'cn': 'china',
        'sa': 'saudi arabia',
        'tr': 'turkey',
        'se': 'sweden',
        'no': 'norway',
        'dk': 'denmark',
        'fi': 'finland',
        'is': 'iceland',
        'gb': 'united kingdom',
        'ca': 'canada',
        'au': 'australia',
        'br': 'brazil',
        'mx': 'mexico',
        'ar': 'argentina',
        'in': 'india'
      };
      
      const country = countryISOMap[countryCode] || null;
      console.log(`🌍 Paese rilevato da IP: ${data.country_name} (${countryCode}) → ${country}`);
      
      return country;
    }
    
    return null;
  } catch (error) {
    console.error('❌ Errore rilevamento paese da IP:', error);
    // Fallback alla lingua del browser
    return getCountryFromLanguage();
  }
};
